import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Platform, View} from 'react-native';
import Animated from 'react-native-reanimated';
import {useHeaderHeight} from '@react-navigation/elements';
import {useFocusEffect, useIsFocused} from '@react-navigation/native';
import {Image, ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react';

import DepartmentCarousel from '../../components/DepartmentCarousel';
import HomepageCarousel from '../../components/HomepageCarousel';
import {
  BANNER_ASPECT_RATIO_MOBILE,
  BANNER_ASPECT_RATIO_WEB,
  BANNER_DESKTOP_MAX_HEIGHT,
  BANNER_DESKTOP_MAX_WIDTH,
} from '../../components/HomepageCarousel/constants';
import {MemberPriceCarousel} from '../../components/ProductCarousel';
import PromotionsCarousel from '../../components/PromotionsCarousel';
import ShopLoyaltyEnabled from '../../components/rewards/ShopLoyaltyEnabled';
import TitleLine from '../../components/rewards/TitleLine';
import ShopByBrand from '../../components/ShopByBrand';
import {BannerSkeleton} from '../../components/skeleton';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {FOOTER_PARENT_STYLE, SiteFooter, usePageBleed} from '../../components/WebFooter/SiteFooter';
import config from '../../config';
import {space} from '../../design-system/tokens';
import {useScrollCollapseHandler} from '../../hooks/useScrollCollapse';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtableBrand, AirtablePromotionItem} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {GeocodedSuburb} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useHomepage} from '../../queries/homepage/useHomepage';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventScreenName} from '../../utils/analytics/events';
import {openPageLink} from '../../utils/navigation';
import {brandRewardsCopy} from '../../utils/rewards';
import SuburbSelectionModal from '../storeSelection/SuburbSelectionModal';

import Banner from './Banner';
import {formatHeroImage} from './shared';

// SHORTCUT: 46 is off the spacing scale; no token (as NonEcomNotice's blockDesktop). Upgrade
// path: DIS-1735 adds a page-top offset token, or design moves it onto the scale.
const PAGE_TOP_DESKTOP = 46;

const modalBodyText = `All of our stores are independently owned and operated. \
To view accurate pricing, promotions and product availability, \
find your local ${config.APP_NAME} store by entering your suburb or postcode below.`;

const HomepageScreen = observer(({navigation, route}: RouteNavigatorScreenProps<AppRoute.HOME>) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const pageBleed = usePageBleed();
  const tabBarPadding = useTabBarBottomPadding({skipFocusReset: true});
  const {shopSelectionStore, userStore, rewardsStore} = useStores();
  const listRef = useRef(null);
  const {scrollHandler} = useScrollCollapseHandler(listRef);
  const headerHeight = useHeaderHeight();
  const [isSuburbModalVisible, setIsSuburbModalVisible] = useState(false);

  const selectedStore = shopSelectionStore?.selectedStore;
  const isFocused = useIsFocused();
  // Force the blocking modal open only while this screen is the focused onboarding
  // surface. SuburbSelectionModal portals to root, so without the focus gate it
  // floats above StoreSelectionScreen (pushed when a suburb is picked) and traps
  // the user — non-dismissable, store list unreachable (DIS-1344 web regression).
  const showBlockingModal = !selectedStore && isFocused;

  // Open modal when navigated with openSuburbModal param
  useFocusEffect(
    useCallback(() => {
      if (route.params?.openSuburbModal) {
        setIsSuburbModalVisible(true);
        navigation.setParams({openSuburbModal: undefined});
      }
    }, [route.params?.openSuburbModal, navigation]),
  );

  // DIS-1859: a sign-up Success CTA with no store sent the user here first.
  // Once a store is picked, ROOT mounts with Home: continue to sign-in. The
  // route-name gate keeps the store-flow instance of this screen
  // (MY_STORE_SUBURB_SELECTION) from consuming the intent before ROOT mounts.
  // Navigate with this screen's own navigation: the effect runs while the
  // store gate in App.tsx swaps in ROOT, when the root ref is not ready and
  // RootNavigation.reset drops the action (measured live on web).
  const signInAfterStoreSelection = userStore.signInAfterStoreSelection;
  useEffect(() => {
    if (route.name !== AppRoute.HOME || !selectedStore || !signInAfterStoreSelection) return;
    userStore.setSignInAfterStoreSelection(null);
    if (!userStore.isLoggedIn) {
      navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
    }
  }, [route.name, selectedStore, signInAfterStoreSelection, userStore, navigation]);

  const handleSelectSuburb = (suburb: GeocodedSuburb) => {
    shopSelectionStore.resetLoading();
    shopSelectionStore.selectSuburb(suburb);

    Analytics.logEvent({
      eventName: EventName.LocationSelected,
      parameters: {
        screen_name: EventScreenName.SuburbSelection,
        location_id: `${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`,
        location_value: `${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`,
      },
    });

    navigation.navigate(AppRoute.MY_STORE_STORE_SELECTION);
    setIsSuburbModalVisible(false);
  };

  const handleCancelSuburbModal = () => {
    setIsSuburbModalVisible(false);
  };

  const {data: homepage, isLoading} = useHomepage({
    storeNumber: selectedStore?.store_number,
    storeState: selectedStore?.address?.administrative_area?.toUpperCase(),
  });

  const alertBannerItem = homepage?.data?.AlertBanner;
  // The AlertBanner image renders inline on the homepage whenever the record
  // carries an image, independent of any message (restores the pre-DIS-1457
  // behaviour). A text alert additionally shows in the global strip
  // (GlobalAlertBanner) — the two are not mutually exclusive.
  const alertHasImage = Boolean(
    alertBannerItem?.heroImage?.url || alertBannerItem?.heroImageWeb?.url,
  );

  // Desktop: Figma homecontent 474:21829 starts the content 46 below the header; a non-ecom
  // store's notice already carries that 46 (NonEcomNotice). Mobile web: the section gap (24) below
  // the header and its search band (QA 2026-10-22). Native keeps the hero flush.
  const pageTop = isWebDesktopScreen
    ? shopSelectionStore.ecommerceDisabled
      ? 0
      : PAGE_TOP_DESKTOP
    : Platform.OS === 'web'
      ? space.s24
      : 0;

  const navigateToRewards = () => navigation.navigate(AppRoute.REWARDS);
  const navigateToDiscover = () => navigation.navigate(AppRoute.DISCOVER);

  const navigateFromCarousel = (pageLink: string) => {
    openPageLink(pageLink);
  };

  return (
    <ThemeProvider theme={rewardsTheme}>
      {/* Drop the shared ContentView top pad on the homepage only: the ScrollView
          reserves the header plus `pageTop` via paddingTop. FOOTER_PARENT_STYLE drops the
          web bottom pad under the footer. SHORTCUT: the 0 is a reset with no space token. */}
      <ContentView testID="home-screen" parentStyle={{paddingTop: 0, ...FOOTER_PARENT_STYLE}}>
        <Animated.ScrollView
          style={pageBleed}
          ref={listRef}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          contentContainerStyle={{
            gap: isWebDesktopScreen ? 40 : 24,
            paddingTop: headerHeight + pageTop,
            paddingBottom: tabBarPadding,
          }}
          showsVerticalScrollIndicator={isWebDesktopScreen}
          {...(Platform.OS === 'android' && {overScrollMode: 'never'})}>
          {alertHasImage ? <Banner bannerItem={alertBannerItem} /> : null}
          <Banner bannerItem={homepage?.data?.TopBanner} />

          {isLoading ? (
            <View>
              <BannerSkeleton
                showDots
                aspectRatio={
                  isWebDesktopScreen ? BANNER_ASPECT_RATIO_WEB : BANNER_ASPECT_RATIO_MOBILE
                }
                maxHeight={isWebDesktopScreen ? BANNER_DESKTOP_MAX_HEIGHT : undefined}
              />
            </View>
          ) : homepage?.carousel?.slides?.length ? (
            <View>
              <HomepageCarousel
                slides={homepage?.carousel?.slides ?? []}
                onSlidePress={navigateFromCarousel}
                testID="featured-carousel"
              />
            </View>
          ) : null}

          <ShopLoyaltyEnabled>
            {rewardsStore.isHydrated && !(userStore.isLoggedIn && rewardsStore.hasRewards) ? (
              <View>
                <TitleLine
                  title={brandRewardsCopy.loyaltyProgramName.toUpperCase()}
                  onSeeAllPress={navigateToRewards}
                />
                <Image
                  onPress={navigateToRewards}
                  source={formatHeroImage(homepage?.data?.LoyaltyAd, isWebDesktopScreen)}
                  containerStyle={{
                    width: '100%',
                    aspectRatio: isWebDesktopScreen
                      ? BANNER_ASPECT_RATIO_WEB
                      : BANNER_ASPECT_RATIO_MOBILE,
                    flex: 1,
                    flexGrow: 1,
                    borderRadius: 10,
                    ...(isWebDesktopScreen
                      ? {
                          maxHeight: BANNER_DESKTOP_MAX_HEIGHT,
                          maxWidth: BANNER_DESKTOP_MAX_WIDTH,
                          alignSelf: 'center',
                        }
                      : {}),
                  }}
                />
              </View>
            ) : null}
            {!shopSelectionStore.ecommerceDisabled && (
              <MemberPriceCarousel
                title={brandRewardsCopy.memberPublicPricingTitle.toUpperCase()}
                member_price="public"
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                    external_sell_type: 'members-price',
                  });
                }}
              />
            )}
          </ShopLoyaltyEnabled>

          <PromotionsCarousel
            onPromotionPress={(promotion: AirtablePromotionItem) => {
              navigation.navigate(AppRoute.PROMOTION, {
                promotion,
              });
            }}
            onSeeAllPress={() => {
              navigation.navigate(AppRoute.PROMOTIONS_CURRENT);
            }}
          />

          {!shopSelectionStore.ecommerceDisabled && (
            <DepartmentCarousel
              departmentName="whats-new"
              onSeeAllPress={department => {
                navigation.navigate(AppRoute.DEPARTMENT, {
                  department,
                });
              }}
            />
          )}

          <View>
            <TitleLine title="DISCOVER" onSeeAllPress={navigateToDiscover} />
            <Image
              onPress={navigateToDiscover}
              source={formatHeroImage(homepage?.data?.DiscoverFeature, isWebDesktopScreen)}
              containerStyle={{
                width: '100%',
                aspectRatio: isWebDesktopScreen
                  ? BANNER_ASPECT_RATIO_WEB
                  : BANNER_ASPECT_RATIO_MOBILE,
                flex: 1,
                flexGrow: 1,
                borderRadius: 10,
                ...(isWebDesktopScreen
                  ? {
                      maxHeight: BANNER_DESKTOP_MAX_HEIGHT,
                      maxWidth: BANNER_DESKTOP_MAX_WIDTH,
                      alignSelf: 'center',
                    }
                  : {}),
              }}
            />
          </View>

          <ShopByBrand
            isLoading={isLoading}
            brands={homepage?.data?.HeroBrands?.brands || []}
            onSeeAllPress={() => {
              navigation.navigate(AppRoute.FAVOURITE_BRANDS);
            }}
            onBrandPress={(brand: AirtableBrand) => {
              navigation.navigate(AppRoute.BRAND, {
                brand,
              });
            }}
          />

          <SiteFooter />
        </Animated.ScrollView>

        <SuburbSelectionModal
          isVisible={showBlockingModal || isSuburbModalVisible}
          onSelectSuburb={handleSelectSuburb}
          onCancel={selectedStore ? handleCancelSuburbModal : undefined}
          initialSearchText={signInAfterStoreSelection?.suburbSearch}
          title="FIND YOUR STORE"
          bodyText={modalBodyText}
        />
      </ContentView>
    </ThemeProvider>
  );
});

export default HomepageScreen;
